import React, { useState, useEffect, useMemo } from "react";
import PropertyCard from "../components/PropertyCard";
import PropertyMap from "../components/PropertyMap";
import useReveal from "../hooks/useReveal";
import { PROPERTY_STATUSES } from "../mock/mock";
import { listProperties, listPropertyCategories } from "../lib/api";
import { LayoutGrid, Map as MapIcon, Search, Maximize2, X } from "lucide-react";

const TYPE_TABS = [
  { value: "all", label: "Toutes" },
  { value: "revente", label: "Revente" },
  { value: "prevente", label: "Prévente" },
];
const LISTING_TABS = [
  { value: "all", label: "Tout" },
  { value: "achat", label: "À acheter" },
  { value: "location", label: "À louer" },
];
const SORTS = [
  { value: "recent", label: "Plus récents" },
  { value: "old", label: "Moins récents" },
  { value: "price_asc", label: "Prix croissant" },
  { value: "price_desc", label: "Prix décroissant" },
];

const PAGE_SIZE = 24;

export default function Properties() {
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [listing, setListing] = useState("all");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("recent");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("grid");
  const [fullscreen, setFullscreen] = useState(false);
  const [visible, setVisible] = useState(PAGE_SIZE);
  useReveal([mode, items, visible]);

  useEffect(() => { setVisible(PAGE_SIZE); }, [type, status, listing, category, q, sort]);

  useEffect(() => {
    listPropertyCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listProperties({ type, status, listing, category, q })
      .then((d) => { if (!cancelled) setItems(d); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [type, status, listing, category, q]);

  const sorted = useMemo(() => {
    const arr = [...items];
    const hasP = (x) => x.price !== null && x.price !== undefined && x.price !== "";
    if (sort === "price_asc" || sort === "price_desc") {
      arr.sort((a, b) => {
        // listings with no price always go last
        if (!hasP(a) && !hasP(b)) return 0;
        if (!hasP(a)) return 1;
        if (!hasP(b)) return -1;
        return sort === "price_asc" ? a.price - b.price : b.price - a.price;
      });
    } else if (sort === "old") {
      arr.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    } else {
      arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return arr;
  }, [items, sort]);

  const selectClass = "px-4 py-2.5 rounded-md border border-slate-200 text-sm text-slate-700 bg-white focus:outline-none focus:border-[#1156a7]";

  return (
    <div>
      {/* header banner */}
      <section className="relative pt-36 pb-14 bg-[#0b1f33] overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: "url(/media/brand-poster.jpg)" }} />
        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <p className="text-[#5b96dd] tracking-[0.3em] text-xs uppercase mb-3">Nos offres immobilières</p>
          <h1 className="font-serif-display text-4xl md:text-5xl text-white">Propriétés au Mexique</h1>
        </div>
      </section>

      {/* Toolbar */}
      <section className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-100 py-4">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 justify-between">
            {/* search */}
            <div className="relative flex-1 max-w-md">
              <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Ville, adresse, code postal…"
                className="w-full pl-10 pr-4 py-2.5 rounded-md border border-slate-200 text-sm focus:outline-none focus:border-[#1156a7]"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={selectClass}>
                <option value="all">Toutes catégories</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={sort} onChange={(e) => setSort(e.target.value)} className={selectClass}>
                {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              {/* mode toggle */}
              <div className="flex gap-1 bg-slate-100 p-1 rounded-md">
                <button onClick={() => setMode("grid")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-600 ${mode === "grid" ? "bg-white text-[#1156a7] shadow-sm" : "text-slate-500"}`}><LayoutGrid size={15} /> Grille</button>
                <button onClick={() => setMode("map")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-600 ${mode === "map" ? "bg-white text-[#1156a7] shadow-sm" : "text-slate-500"}`}><MapIcon size={15} /> Carte</button>
              </div>
            </div>
          </div>
          {/* filter chips */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {LISTING_TABS.map((t) => (
              <button key={t.value} onClick={() => setListing(t.value)} className={`px-4 py-1.5 rounded-full text-xs font-600 transition-colors ${listing === t.value ? "bg-[#1156a7] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{t.label}</button>
            ))}
            <span className="w-px h-5 bg-slate-200 mx-1" />
            {TYPE_TABS.map((t) => (
              <button key={t.value} onClick={() => setType(t.value)} className={`px-4 py-1.5 rounded-full text-xs font-600 transition-colors ${type === t.value ? "bg-[#0b1f33] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{t.label}</button>
            ))}
            <span className="w-px h-5 bg-slate-200 mx-1" />
            <button onClick={() => setStatus("all")} className={`px-3 py-1.5 rounded-full text-xs font-600 ${status === "all" ? "bg-[#5b96dd] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>Tous statuts</button>
            {PROPERTY_STATUSES.map((s) => (
              <button key={s.value} onClick={() => setStatus(s.value)} className={`px-3 py-1.5 rounded-full text-xs font-600 ${status === s.value ? "bg-[#5b96dd] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{s.label}</button>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-3">
            <span className="font-700 text-[#0b1f33]">{loading ? "…" : sorted.length}</span> propriété{sorted.length > 1 ? "s" : ""} trouvée{sorted.length > 1 ? "s" : ""}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-slate-50 min-h-[50vh]">
        <div className="max-w-6xl mx-auto px-6">
          {mode === "grid" ? (
            loading ? (
              <p className="text-center text-slate-400 py-16">Chargement des propriétés…</p>
            ) : sorted.length === 0 ? (
              <p className="text-center text-slate-400 py-16">Aucune propriété ne correspond à ces critères.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {sorted.slice(0, visible).map((p) => (
                  <div key={p.id}>
                    <PropertyCard property={p} />
                  </div>
                ))}
              </div>
            )
          ) : !fullscreen ? (
            <div className="relative">
              <button
                onClick={() => setFullscreen(true)}
                className="absolute top-3 right-3 z-[500] flex items-center gap-1.5 bg-white shadow px-3 py-2 rounded-md text-sm font-600 text-[#1156a7] hover:bg-slate-50"
              >
                <Maximize2 size={15} /> Plein écran
              </button>
              <PropertyMap properties={sorted} />
            </div>
          ) : (
            <div className="h-[440px] rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 text-sm">
              Carte en plein écran…
            </div>
          )}
          {mode === "grid" && !loading && visible < sorted.length && (
            <div className="flex flex-col items-center gap-3 mt-12">
              <p className="text-sm text-slate-500" data-testid="properties-shown-count">{Math.min(visible, sorted.length)} sur {sorted.length} propriétés affichées</p>
              <button onClick={() => setVisible((v) => v + PAGE_SIZE)} data-testid="properties-load-more" className="btn-gold px-7 py-3 rounded-md font-600 text-sm">
                Afficher plus de propriétés
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Fullscreen map */}
      {fullscreen && (
        <div className="fixed inset-0 z-[999] bg-white">
          <button
            onClick={() => setFullscreen(false)}
            className="absolute top-4 right-4 z-[1000] flex items-center gap-1.5 bg-[#0b1f33] text-white px-4 py-2.5 rounded-md text-sm font-600 shadow-lg"
          >
            <X size={16} /> Fermer
          </button>
          <PropertyMap properties={sorted} height="100vh" />
        </div>
      )}
    </div>
  );
}
